import { Input, Textarea } from '@heroui/react'
import PathwayGenLoader from 'assets/images/pathway_gen.png'
import PathwayGenLogo from 'assets/images/pathwayGen.png'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'

const sampleTemplates = {
  'Restaurant Reservations': 'Restaurant Reservations',
  'Tech Support': 'Tech Support',
  'Lead Qualification': 'Lead Qualification',
}

export default function PathwayGenerationLoader({ pathwayName, useCase, agentSpeech }) {
  const [progress, setProgress] = useState(0)

  // Configuration for slower progression
  const MAX_PROGRESS = 99 // Hard cap so we practically never hit 100%
  const TIME_TO_MAX = 150000 // ~150 s (2.5 min) to approach MAX_PROGRESS
  const [textKey, setTextKey] = useState(0) // For triggering animations

  // Dynamic text based on progress
  const getLoadingText = () => {
    if (progress < 20) {
      return {
        title: 'Analyzing Your Requirements',
        subtitle: 'Understanding your use case and objectives...',
      }
    }
    else if (progress < 40) {
      return {
        title: 'Designing Conversation Flow',
        subtitle: 'Creating dialogue branches and decision points...',
      }
    }
    else if (progress < 60) {
      return {
        title: 'Building Response Logic',
        subtitle: 'Implementing intelligent responses and fallbacks...',
      }
    }
    else if (progress < 80) {
      return {
        title: 'Optimizing Interactions',
        subtitle: 'Fine-tuning conversation patterns...',
      }
    }
    else {
      return {
        title: 'Finalizing Your Pathway',
        subtitle: 'Applying finishing touches and optimizing every prompt',
      }
    }
  }

  const loadingText = getLoadingText()
  useEffect(() => {
    // Progress through stages at fixed intervals over 60 seconds
    const startTime = Date.now()

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        let next = prev
        const timeElapsed = Date.now() - startTime
        const timeProgress = timeElapsed / TIME_TO_MAX

        if (prev < 95) {
          // Base progress with randomness
          const baseIncrement = 0.5 + Math.random() * 1.5 // 0.5 to 2 per second

          // Occasional larger jumps
          const shouldJump = Math.random() < 0.08 // 8% chance of jump

          const jumpAmount = shouldJump ? (3 + Math.random() * 5) : 0 // 3-8 point jumps

          // Target progress based on time (with some randomness)
          const targetProgress = Math.min(
            MAX_PROGRESS - 5, // stay a few % under the cap during main phase
            timeProgress * (MAX_PROGRESS - 5) + (Math.random() - 0.5) * 10,
          )

          // If we're significantly behind target, catch up faster
          if (prev < targetProgress - 15) {
            next = prev + baseIncrement + jumpAmount + (Math.random() * 3)
          }
          else {
            next = prev + baseIncrement + jumpAmount
          }

          // Add some stalls (15% chance to not progress)
          if (Math.random() < 0.15 && prev > 10) {
            next = prev
          }

          // Ensure we don't exceed MAX_PROGRESS – 4 until the very end
          next = Math.min(MAX_PROGRESS - 4, next)
        }
        else {
          // Crawl from 95->99 very slowly; asymptotically approaches MAX_PROGRESS
          if (timeElapsed > TIME_TO_MAX + 30000) {
            next = prev + (MAX_PROGRESS - prev) * 0.01
          }
          else {
            next = prev + (MAX_PROGRESS - prev) * 0.002
          }
        }

        // Trigger text animation on crossing thresholds
        if (
          (prev < 20 && next >= 20)
          || (prev < 40 && next >= 40)
          || (prev < 60 && next >= 60)
          || (prev < 80 && next >= 80)
        ) {
          setTextKey(k => k + 1)
        }

        return Math.min(MAX_PROGRESS, next)
      })
    }, 1000) // Update every second for smoother animation

    // Initial progress with small random start
    setProgress(2 + Math.random() * 4)

    return () => {
      clearInterval(progressInterval)
    }
  }, [])

  return (
    <div className="fixed inset-0 z-1000 overflow-auto bg-white">
      <div className="flex min-h-screen flex-col">

        {/* Main Content */}
        <div className={`
          grid min-h-[calc(100vh-140px)] flex-1 grid-cols-1
          lg:grid-cols-2
        `}
        >
          {/* Left Panel - Form */}
          <div className={`
            overflow-y-auto border-r border-gray-200 bg-white p-6
            lg:p-10
            xl:p-12
          `}
          >
            {/* Form Header */}
            <div className={`
              mx-auto mt-10 mb-8 max-w-md text-center
              xl:max-w-lg
            `}
            >
              <img
                src={PathwayGenLogo}
                alt="Pathway Gen Icon"
                className="mx-auto mb-3 h-12 w-12"
              />
              <h2 className={`
                mb-2 text-2xl font-semibold text-gray-900
                xl:text-3xl
              `}
              >
                Generate Pathway
              </h2>
              <p className={`
                text-sm text-gray-600
                xl:text-base
              `}
              >
                Create a new pathway from a use case or a script, or a pathway JSON, or generate one from scratch
              </p>
            </div>

            {/* Form Content */}
            <div className={`
              mx-auto flex max-w-lg flex-col gap-y-6
              xl:max-w-xl
            `}
            >
              <div>
                <Input
                  placeholder="Enter Pathway Name"
                  radius="sm"
                  variant="bordered"
                  value={pathwayName}
                  readOnly
                  disabled
                  className="bg-gray-50"
                />
              </div>

              <div>
                <label className={`
                  mb-2 block text-sm font-medium text-gray-900
                  xl:text-base
                `}
                >
                  Define the Use Case
                </label>
                <Textarea
                  placeholder="Describe what this AI call is supposed to do..."
                  radius="sm"
                  variant="bordered"
                  minRows={6}
                  value={useCase}
                  readOnly
                  disabled
                  className="bg-gray-50"
                />
              </div>

              <div>
                <label className={`
                  mb-2 block text-sm font-medium text-gray-900
                  xl:text-base
                `}
                >
                  Provide Examples of Agent Speech
                </label>
                <Textarea
                  placeholder="Describe the personality and tone..."
                  radius="sm"
                  variant="bordered"
                  minRows={4}
                  value={agentSpeech}
                  readOnly
                  disabled
                  className="bg-gray-50"
                />
              </div>

              {/* Template Section */}
              <div className="border-t border-gray-200 pt-4">
                <div className={`
                  flex items-center gap-3 text-xs
                  xl:text-sm
                `}
                >
                  <span className="whitespace-nowrap text-gray-500">💡 Quick start:</span>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(sampleTemplates).map(([title]) => (
                      <button
                        key={title}
                        disabled
                        className={`
                          cursor-not-allowed rounded border border-gray-200
                          bg-gray-50 px-2 py-1 text-xs text-gray-400 opacity-70
                          xl:text-sm
                        `}
                      >
                        {title}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Preview with Progress */}
          <div className={`
            relative flex flex-col items-center justify-center overflow-hidden
            p-6
            lg:p-10
          `}
          >
            {/* Dotted Background Pattern */}
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `radial-gradient(circle, #9ca3af 1px, transparent 1px)`,
                backgroundSize: '24px 24px',
              }}
            />

            {/* Center Content - Loading Messages and Image */}
            <div className={`
              relative z-10 flex flex-1 flex-col items-center justify-center
            `}
            >
              {/* Pathway Image with Text Overlay */}
              <div className="relative">
                <img
                  src={PathwayGenLoader}
                  alt="Pathway Generation Preview"
                  className="relative z-10"
                  style={{ width: '924px', height: '604px' }}
                />
                {/* Text Overlay - Centered on Image */}
                <div className={`
                  absolute inset-0 z-20 flex items-center justify-center
                  overflow-hidden
                `}
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={textKey}
                      className="text-center"
                      initial={{ opacity: 0, x: 100, scale: 0.9 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      exit={{ opacity: 0, x: -100, scale: 0.9 }}
                      transition={{
                        duration: 0.6,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                    >
                      <motion.h3
                        className={`
                          mb-2 text-xl font-semibold text-gray-900
                          xl:text-2xl
                        `}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.4 }}
                      >
                        {loadingText.title}
                      </motion.h3>
                      <motion.p
                        className={`
                          text-sm text-gray-600
                          xl:text-base
                        `}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                      >
                        {loadingText.subtitle}
                      </motion.p>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar - Bottom of Right Panel Only */}
      <div className={`
        absolute right-0 bottom-0 left-0 z-20
        lg:left-1/2
      `}
      >
        <div className="relative">
          <div className="h-2 w-full overflow-hidden bg-gray-100">
            <motion.div
              className={`
                relative h-full bg-linear-to-r from-emerald-500 to-emerald-600
              `}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            >
              {/* Shimmer effect */}
              <motion.div
                className={`
                  absolute inset-0 bg-linear-to-r from-transparent via-white
                  to-transparent opacity-30
                `}
                animate={{ x: ['0%', '100%'] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                style={{ width: '100%' }}
              />
            </motion.div>
          </div>
          {/* Centered percentage for right panel */}
          <div className={`
            absolute right-0 bottom-full left-0 mb-3 flex justify-center
            lg:left-1/2 lg:-translate-x-1/2
          `}
          >
            <motion.div
              className="rounded-lg bg-white px-4 py-2 shadow-lg"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className={`
                mb-1 text-lg font-semibold text-gray-700
                xl:text-xl
              `}
              >
                {Math.round(progress)}
                % Complete
              </div>
              <p className={`
                text-xs text-gray-500
                xl:text-sm
              `}
              >
                This typically takes a couple of minutes
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}
